// src/features/dashboard/pages/ProfilePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrentUserProfile, updateCurrentUserProfile } from '../../../services/accountService';
import useApi from '../../../hooks/useApi';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Save,
  X,
  Shield,
  CheckCircle,
  Clock
} from 'lucide-react';

const ProfilePage = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  const emptyForm = {
    id: '',
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    is_staff: false,
    is_active: true,
    active_status: 'active',
    created_at: '',
    updated_at: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  const { callApi: fetchProfile, loading } = useApi(getCurrentUserProfile, {
    showSuccessToast: false,
    showErrorToast: true,
  });

  const { callApi: saveProfile } = useApi(updateCurrentUserProfile, {
    showSuccessToast: true,
    showErrorToast: true,
    msg: 'Profile updated successfully',
  });

  // Normalize API data — converts null → '' so inputs are always controlled
  const normalize = (data) => ({
    id: data.id ?? '',
    username: data.username ?? '',
    first_name: data.first_name ?? '',
    last_name: data.last_name ?? '',
    email: data.email ?? '',
    phone: data.phone ?? '',
    address: data.address ?? '',
    is_staff: data.is_staff ?? false,
    is_active: data.is_active ?? true,
    active_status: data.active_status ?? 'active',
    created_at: data.created_at ?? '',
    updated_at: data.updated_at ?? '',
  });

  // Fetch profile on mount
  useEffect(() => {
    const load = async () => {
      const data = await fetchProfile();
      if (data) {
        const normalized = normalize(data);
        setFormData(normalized);
        setOriginalData(normalized);
      }
    };
    load();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit updated profile
  const handleSubmit = async (e) => {
    e.preventDefault();
    const updated = await saveProfile({
      username: formData.username,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone || null,
      address: formData.address || null,
    });
    if (updated) {
      const normalized = normalize(updated);
      setFormData(normalized);
      setOriginalData(normalized);
      setEditing(false);
    }
  };

  // Cancel editing — restore last saved data
  const handleCancel = () => {
    if (originalData) setFormData(originalData);
    setEditing(false);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    const firstName = formData.first_name || '';
    const lastName = formData.last_name || '';
    if (firstName && lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    if (formData.username) return formData.username.charAt(0).toUpperCase();
    return 'U';
  };

  // Get full name
  const getFullName = () => {
    const firstName = formData.first_name || '';
    const lastName = formData.last_name || '';
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return formData.username || 'User';
  };

  // Get status badge color
  const getStatusColor = () => {
    if (!formData.is_active) return 'bg-red-100 text-red-800';
    if (formData.active_status === 'active') return 'bg-green-100 text-green-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-200  via-gray-50 to-primary-50 rounded-2xl">
      {/* <div className="max-w-6xl mx-auto px-4 py-8"> */}

        {/* Header Section */}
        <div className="rounded-2xl  overflow-hidden mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-primary-600/10"></div>
            <div className="relative px-8 py-12">
              <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">

                {/* Avatar */}
                <div className="relative">
                  <div className="h-32 w-32 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 shadow-2xl flex items-center justify-center ring-4 ring-white">
                    <span className="text-4xl font-bold text-white">{getUserInitials()}</span>
                  </div>
                  <div className="absolute -bottom-2 -right-2">
                    <div className={`h-8 w-8 rounded-full ${getStatusColor()} border-4 border-white flex items-center justify-center`}>
                      <CheckCircle size={16} />
                    </div>
                  </div>
                </div>

                {/* User Info */}
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">{getFullName()}</h1>
                  <p className="text-xl text-gray-600 mb-4">@{formData.username}</p>

                  <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-6">
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor()}`}>
                      {formData.is_active ? formData.active_status : 'Inactive'}
                    </span>
                    {formData.is_staff && (
                      <span className="px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800 flex items-center">
                        <Shield size={14} className="mr-1" />
                        Staff Member
                      </span>
                    )}
                    <span className="px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      ID: {formData.id}
                    </span>
                  </div>

                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-3 rounded-xl shadow-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 flex items-center font-medium mx-auto md:mx-0"
                    >
                      <Edit size={18} className="mr-2" />
                      Edit Profile
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Profile Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-primary-50 px-8 py-6 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
                <p className="text-gray-600 mt-1">Manage your personal information and preferences</p>
              </div>

              <div className="p-8">
                {editing ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      {/* Username (read-only) */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <User size={18} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="username"
                            value={formData.username}
                            className="block w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed"
                            disabled
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Mail size={18} className="text-gray-400" />
                          </div>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="block w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                            placeholder="your.email@example.com"
                          />
                        </div>
                      </div>

                      {/* First Name */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                        <input
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="First Name"
                        />
                      </div>

                      {/* Last Name */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                        <input
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="Last Name"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Phone size={18} className="text-gray-400" />
                          </div>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="block w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                      </div>

                      {/* Address */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <MapPin size={18} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="block w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                            placeholder="Your address"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                      >
                        <X size={16} className="mr-2" />
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl text-sm font-medium hover:from-primary-600 hover:to-primary-700 flex items-center transition-all duration-200 shadow-lg"
                      >
                        <Save size={16} className="mr-2" />
                        Save Changes
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <InfoRow icon={<User size={20} className="text-primary-600" />} label="Username" value={formData.username} />
                      <InfoRow icon={<User size={20} className="text-primary-600" />} label="First Name" value={formData.first_name} />
                      <InfoRow icon={<User size={20} className="text-primary-600" />} label="Last Name" value={formData.last_name} />
                      <InfoRow icon={<Phone size={20} className="text-primary-600" />} label="Phone Number" value={formData.phone} />
                    </div>
                    <div className="space-y-6">
                      <InfoRow icon={<Mail size={20} className="text-primary-600" />} label="Email Address" value={formData.email} />
                      <InfoRow icon={<MapPin size={20} className="text-primary-600" />} label="Address" value={formData.address} />
                      <InfoRow icon={<Shield size={20} className="text-primary-600" />} label="Account Type" value={formData.is_staff ? 'Staff Member' : 'Regular User'} />
                      <InfoRow
                        icon={<CheckCircle size={20} className="text-primary-600" />}
                        label="Account Status"
                        value={formData.is_active ? formData.active_status : 'Inactive'}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">

            {/* Account Timeline */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-primary-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">Account Timeline</h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Calendar size={16} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Account Created</p>
                    <p className="text-gray-900 font-semibold text-sm">
                      {formData.created_at
                        ? new Date(formData.created_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric',
                          })
                        : 'Not specified'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Clock size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Last Updated</p>
                    <p className="text-gray-900 font-semibold text-sm">
                      {formData.updated_at
                        ? new Date(formData.updated_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric',
                          })
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Summary */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-primary-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">Account Summary</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">User ID</span>
                  <span className="font-semibold text-gray-900">#{formData.id}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
                    {formData.is_active ? formData.active_status : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Account Type</span>
                  <span className="font-semibold text-gray-900">
                    {formData.is_staff ? 'Staff' : 'User'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      {/* </div> */}
    </div>
  );
};

// Small helper component to avoid repetition in view mode
const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start space-x-4">
    <div className="bg-primary-100 p-3 rounded-xl flex-shrink-0">{icon}</div>
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
      <p className="text-gray-900 font-semibold">{value || 'Not specified'}</p>
    </div>
  </div>
);

export default ProfilePage;